// Compute left/right track boundary points from a centerline with widths.
//
// Improvements over naive perpendicular offset:
//   A) Wide-window curvature estimation (±CURV_HALF_WIN points) for stable
//      inner-side clamping in tight corners.
//   C) Post-process each boundary to remove self-intersection loops.

const CURV_HALF_WIN = 8 // points on each side for curvature estimation
const CURV_CLAMP_FACTOR = 0.7 // max inner offset = radius × this factor
const INTERSECTION_LOOKBACK = 40 // how many prior segments to check for crossings

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Compute left and right boundary polylines from a centerline.
 *
 * @param {Array} cl  – centerline: [[x, y, z, wL, wR], …]
 * @param {Function} project – world projection: (x, y, z) → [screenX, screenY]
 * @returns {{ left: number[][], right: number[][], rawLeft: number[][], rawRight: number[][], leftWorld: number[][], rightWorld: number[][] }}
 */
export function computeTrackBoundaries(cl, project) {
  const rawLeft = []
  const rawRight = []
  const leftWorld = []
  const rightWorld = []

  for (let i = 0; i < cl.length; i++) {
    const [x, y, z, wL, wR] = cl[i]

    // Tangent from neighbors (ground-plane only — altitude doesn't enter
    // the perpendicular-offset calculation; it is reapplied by `project`).
    let dx, dz
    if (i === 0) {
      dx = cl[1][0] - cl[0][0]
      dz = cl[1][2] - cl[0][2]
    } else if (i === cl.length - 1) {
      dx = cl[i][0] - cl[i - 1][0]
      dz = cl[i][2] - cl[i - 1][2]
    } else {
      dx = cl[i + 1][0] - cl[i - 1][0]
      dz = cl[i + 1][2] - cl[i - 1][2]
    }

    const len = Math.sqrt(dx * dx + dz * dz) || 1
    const nx = -dz / len
    const nz = dx / len

    // Wide-window curvature estimation (A)
    let effL = wL
    let effR = wR
    const curvature = estimateCurvature(cl, i)
    if (curvature !== 0) {
      const radius = Math.abs(1 / curvature)
      const maxInner = radius * CURV_CLAMP_FACTOR
      if (curvature > 0) effL = Math.min(effL, maxInner)
      else effR = Math.min(effR, maxInner)
    }

    const lx = x + nx * effL,
      lz = z + nz * effL
    const rx = x - nx * effR,
      rz = z - nz * effR
    leftWorld.push([lx, y, lz])
    rightWorld.push([rx, y, rz])
    rawLeft.push(project(lx, y, lz))
    rawRight.push(project(rx, y, rz))
  }

  // Remove self-intersection loops (C)
  const left = removeLoops(rawLeft)
  const right = removeLoops(rawRight)

  return { left, right, rawLeft, rawRight, leftWorld, rightWorld }
}

/**
 * Compute the per-segment quads forming the altitude wall strip beneath the
 * track. Each quad has its top edge on the projected centerline and its
 * bottom edge on the ground reference plane.
 *
 * @param {Array} cl  – centerline: [[x, y, z, wL, wR], …]
 * @param {Function} project – world projection: (x, y, z) → [screenX, screenY]
 * @param {Function} groundProject – ground-plane projection: (x, z) → [screenX, screenY]
 * @returns {{ top: number[][], bottom: number[][] }}
 */
export function computeWallStrip(cl, project, groundProject) {
  const top = new Array(cl.length)
  const bottom = new Array(cl.length)
  for (let i = 0; i < cl.length; i++) {
    const [x, y, z] = cl[i]
    top[i] = project(x, y, z)
    bottom[i] = groundProject(x, z)
  }
  return { top, bottom }
}

// ── Curvature ───────────────────────────────────────────────────────────────

/**
 * Estimate signed curvature at index i using a wider window.
 * Uses points [i - half] and [i + half] instead of immediate neighbors,
 * giving a much more stable estimate over noisy 1 m-spaced centerlines.
 */
function estimateCurvature(cl, i) {
  const n = cl.length
  if (n < 3) return 0

  const half = Math.min(CURV_HALF_WIN, Math.floor((n - 1) / 2))
  const prev = Math.max(0, i - half)
  const next = Math.min(n - 1, i + half)
  if (prev === i || next === i) return 0

  const ax = cl[i][0] - cl[prev][0]
  const az = cl[i][2] - cl[prev][2]
  const bx = cl[next][0] - cl[i][0]
  const bz = cl[next][2] - cl[i][2]
  const cross = ax * bz - az * bx
  const aLen = Math.sqrt(ax * ax + az * az) || 1
  const bLen = Math.sqrt(bx * bx + bz * bz) || 1
  return (2 * cross) / (aLen * bLen * (aLen + bLen))
}

// ── Self-intersection removal ───────────────────────────────────────────────

/**
 * Walk a polyline and remove any self-intersection loops.
 *
 * When an offset curve folds back on itself the resulting polyline contains
 * loops.  We detect these by checking each new segment against recent prior
 * segments.  When two segments cross we snip the loop: replace everything
 * between the two crossing segments with the intersection point.
 */
function removeLoops(pts) {
  if (pts.length < 4) return pts

  const out = [pts[0]]

  for (let i = 1; i < pts.length; i++) {
    const p1 = out[out.length - 1]
    const p2 = pts[i]

    let clipped = false
    const earliest = Math.max(0, out.length - 1 - INTERSECTION_LOOKBACK)
    for (let j = out.length - 2; j >= earliest; j--) {
      const q1 = out[j]
      const q2 = out[j + 1]
      const ix = segmentIntersection(p1, p2, q1, q2)
      if (ix) {
        out.length = j + 1
        out.push(ix)
        clipped = true
        break
      }
    }

    if (!clipped) {
      out.push(p2)
    }
  }

  return out
}

/**
 * Compute the intersection point of segments (a1→a2) and (b1→b2).
 * Returns [x, y] or null if they don't cross.
 */
function segmentIntersection(a1, a2, b1, b2) {
  const dx1 = a2[0] - a1[0]
  const dy1 = a2[1] - a1[1]
  const dx2 = b2[0] - b1[0]
  const dy2 = b2[1] - b1[1]
  const denom = dx1 * dy2 - dy1 * dx2
  if (Math.abs(denom) < 1e-10) return null

  const t = ((b1[0] - a1[0]) * dy2 - (b1[1] - a1[1]) * dx2) / denom
  const u = ((b1[0] - a1[0]) * dy1 - (b1[1] - a1[1]) * dx1) / denom

  if (t > 0 && t < 1 && u > 0 && u < 1) {
    return [a1[0] + t * dx1, a1[1] + t * dy1]
  }
  return null
}
